/**
 * Common utility functions for booking payment calculations
 */

import { PAYMENT_OPTIONS } from "pagesV2/pro/Bookings/constants"
import { isFailedPaymentStatus } from "ui/Booking/Details/utils"

// Rounds a monetary value to cents, eliminating floating-point drift
// (e.g. 1100 - 0 surfacing as 1099.9999999). Without this, validators that
// compare against the balance can reject the true outstanding amount.
export const roundToCents = (value) => Math.round((Number(value) || 0) * 100) / 100

/**
 * Whether the remaining balance will be collected in CASH (so only the deposit is paid by card).
 */
export const isBalanceInCash = (paymentOption) =>
  [PAYMENT_OPTIONS.deposit_cash, PAYMENT_OPTIONS.deposit_link_in_cash].includes(paymentOption)

/**
 * The fraction of the pre-card total that the card processing fee is charged on (passed to
 * calculateBookingTotal as options.depositFraction).
 * - Full payment: the whole total is charged now → fraction 1.
 * - Any deposit: only the deposit is charged now, so the fee applies to the deposit only →
 *   fraction = deposit% / 100. This holds whether the balance is later paid by card or in cash;
 *   the balance earns its own fee when it is actually collected, never up front.
 */
export const getCardFeeFraction = ({ depositOnly, depositPercent, customDepositAmount } = {}) => {
  if (!depositOnly) return 1
  const pct = Number(customDepositAmount || depositPercent) || 0
  return pct > 0 && pct < 100 ? pct / 100 : 1
}

// Stripe's per-charge fixed component. Every card-fee quote includes it, so it lives in one place
// rather than as a literal at each call site.
export const STRIPE_FIXED_FEE = 0.3

/**
 * Normalizes a payment method's `card_fee_info` into the shape grossUpCardFee expects.
 *
 * The backend reliably sends `rate` but not a usable `fixed`, and grossUpCardFee's own default only
 * covers `undefined` — a `null` or `0` falls through to `Number(fixed) || 0` and silently drops
 * $0.30 from the quote. That only showed on edit, where the booking's saved card is auto-selected
 * so the backend object is used verbatim; creation usually has no card yet and took a literal
 * fallback that already carried the $0.30.
 */
export const normalizeCardFeeInfo = (cardFeeInfo, fallbackRate = 0) => {
  const fixed = Number(cardFeeInfo?.fixed)
  return {
    rate: Number(cardFeeInfo?.rate) || Number(fallbackRate) || 0,
    fixed: fixed > 0 ? fixed : STRIPE_FIXED_FEE
  }
}

/**
 * Whether any money has actually been taken on this booking — a confirmed, non-tip payment.
 * payment_history already leaves card authorizations (holds) out; failed, declined and canceled
 * incomes moved nothing, so only `confirmed` counts.
 *
 * Decides whether an edit to cash keeps the booking's original platform and card fees: a fee that
 * was taken is never removed, but on a booking nothing was ever collected on (an expired request)
 * there is nothing to keep, and cash carries neither fee — full_amount_in_cash_on_day zeroes both
 * on the backend.
 */
export const hasCollectedPayment = (booking) =>
  (booking?.payment_history || []).some(
    (payment) => payment?.status?.toLowerCase() === "confirmed" && payment?.type?.toLowerCase() !== "tip" && Number(payment?.amount) > 0
  )

/**
 * The card charge already settled on this booking, as { fee, preCardTotal } — or null when nothing
 * has been collected yet.
 *
 * Raising the price on an edit is collected as a SECOND Stripe charge, and Stripe's fixed $0.30
 * applies per charge, not per booking. Grossing the new grand total up in one go bundles both
 * charges into a single $0.30 and leaves the guide short by one: on a $116 booking topped up with a
 * $100 add-on it quotes $6.76 of card fee, so the guide nets $99.70 on the add-on instead of $100.
 * Keeping the settled fee and grossing up only the increase quotes $7.07 and nets the full $100.
 */
export const getPriorCardCharge = (booking) => {
  const summary = booking?.payment_summary
  const fee = Number(summary?.card_processing_fee) || 0
  if (fee <= 0) return null
  // A fee quoted on an unpaid booking (a payment link nobody has opened) has not been taken by
  // Stripe yet, so there is still only one charge coming and the whole total grosses up normally.
  const hasSettledPayment = (booking?.payment_history || []).some(
    (payment) => !isFailedPaymentStatus(payment?.status) && payment?.type?.toLowerCase() !== "tip" && Number(payment?.amount) > 0
  )
  if (!hasSettledPayment) return null
  const preCardTotal = (Number(summary?.total_price) || 0) - fee
  return preCardTotal > 0 ? { fee, preCardTotal } : null
}

/**
 * The title the backend gives the fee inside payment_summary.custom_fees. This is a PAYLOAD value
 * matched as-is — it is deliberately not renamed with the display label ("Payment Processing Fee").
 */
export const BACKEND_CARD_FEE_TITLE = "Card Processing Fee"

/**
 * The processing fee a SAVED booking carries, read from payment_summary.
 *
 * Two shapes are in play: the backend injects a custom_fees entry titled "Card Processing Fee" on
 * some payloads, and exposes the flat card_processing_fee field on others. Prefer the entry (it is
 * only ever present when the fee is passed on to the guest) and fall back to the flat field, which
 * is the only source on a payload that omits the entry — without the fallback the fee silently
 * disappears from every screen that renders a saved booking.
 */
export const getProcessingFeeFromSummary = (paymentSummary) => {
  const entry = (paymentSummary?.custom_fees ?? []).find((fee) => fee?.title === BACKEND_CARD_FEE_TITLE)
  if (entry) return Math.max(0, Number(entry.price) || 0)
  return Math.max(0, Number(paymentSummary?.card_processing_fee) || 0)
}

/**
 * The two totals every saved-booking screen shows: the booking price (fee excluded, the bold
 * "Total" row) and what the card was actually charged (fee included, the row below it).
 *
 * The two payload shapes differ in where the fee sits relative to total_price: a custom_fees entry
 * is PART of total_price, while the flat card_processing_fee field sits OUTSIDE it. Deriving both
 * totals here keeps every screen correct on either shape — subtracting unconditionally would
 * under-report the Total by the fee on the flat-field payload.
 */
export const getSavedBookingTotals = (paymentSummary) => {
  const processingFee = getProcessingFeeFromSummary(paymentSummary)
  const totalPrice = Number(paymentSummary?.total_price) || 0
  const isFeeInsideTotalPrice = (paymentSummary?.custom_fees ?? []).some(
    (fee) => fee?.title === BACKEND_CARD_FEE_TITLE
  )
  const bookingTotal = isFeeInsideTotalPrice ? totalPrice - processingFee : totalPrice
  return { processingFee, bookingTotal, totalCharged: bookingTotal + processingFee }
}

/**
 * Card processing fee grossed up on `base` so that after Stripe's deduction on (base + fee) the
 * net equals `base`. Returns 0 when passthrough is off, there's no rate, or base <= 0.
 */
export const grossUpCardFee = (base, { rate = 0, fixed = STRIPE_FIXED_FEE, enabled = true } = {}) => {
  const r = Number(rate) || 0
  if (!enabled || r <= 0 || r >= 1 || base <= 0) return 0
  return (base * r + (Number(fixed) || 0)) / (1 - r)
}

// Platform fee and card processing fee are only collected on a price increase when the balance
// will be paid by card (deposit-now + balance-later). For balance-in-cash options no card is
// charged for the extra amount, so neither fee applies.
const FEE_PAYMENT_OPTIONS = [PAYMENT_OPTIONS.deposit_later, PAYMENT_OPTIONS.deposit_link]

// Cash-balance options: the platform fee on a price increase isn't shown (it's collected at
// payment time, and only if the guide takes it by card / payment link).
export const CASH_BALANCE_PAYMENT_OPTIONS = [PAYMENT_OPTIONS.deposit_cash, PAYMENT_OPTIONS.deposit_link_in_cash]

/**
 * Taxes and fees owed on a price increase of `diff`, quoted by the Adjust Booking Price modal.
 *
 * No card processing fee is included: adjusting a price collects no payment, so there is no card
 * transaction to charge a processing fee on, and the method that will eventually settle the
 * increase is not chosen yet. The fee is added in Collect Payment, on the amount actually charged.
 *
 * Returns null when nothing is owed (diff <= 0, or no applicable fees).
 */
export const calculateAdjustmentFeeBreakdown = ({ customFeesData, feeSummaryData, diff, paymentOption }) => {
  if (diff <= 0) return null

  const feeItems = []

  // Trip custom fees (taxes / fees) — percentage of the price increase.
  const customFees = Array.isArray(customFeesData) ? customFeesData : (customFeesData?.custom_fees ?? [])
  for (const fee of customFees) {
    if (fee.fee_type !== "percentage") continue
    feeItems.push({ label: fee.title, amount: (fee.price / 100) * diff })
  }

  // Platform fee (fee_summary) — only when the balance is paid by card.
  if (feeSummaryData && FEE_PAYMENT_OPTIONS.includes(paymentOption)) {
    // Platform fee — guest portion, charged as a percentage of the price increase.
    const platformFeeConfig = feeSummaryData?.platform_fee
    let platformFee = 0
    if (
      platformFeeConfig?.charged_to_guest &&
      platformFeeConfig?.amount_charged_to_guest?.type === "percentage" &&
      Number(platformFeeConfig?.amount_charged_to_guest?.value) > 0
    ) {
      platformFee = (Number(platformFeeConfig.amount_charged_to_guest.value) / 100) * diff
      if (platformFee > 0) feeItems.push({ label: platformFeeConfig?.title ?? "Platform Fee", amount: platformFee })
    }
  }

  if (feeItems.length === 0) return null
  const totalFees = feeItems.reduce((sum, item) => sum + item.amount, 0)
  return { feeItems, totalFees }
}

/**
 * Resolves the card fee config from an experience's settings. `enabled` is true only when the
 * guide passes the fee to the guest (card_fee_passthrough_enabled) and an active "Card Processing
 * Fee" entry exists. `rate` is that entry's percent / 100; `fixed` defaults to Stripe's $0.30.
 */
export const resolveCardFeeConfig = ({ customFees = [], platformFeeSetting } = {}) => {
  const entry = (customFees || []).find((f) => f?.title === "Card Processing Fee" && f?.is_active !== false)
  const enabled = platformFeeSetting?.card_fee_passthrough_enabled === true && !!entry
  return { rate: entry ? Number(entry.price) / 100 : 0, fixed: 0.3, enabled }
}

/**
 * Whether the (possibly newly-selected, in edit mode) experience requires a deposit at booking.
 *
 * In edit mode the guide can swap the booking to a different experience, but the booking's own
 * `payment_option` only reflects whichever experience it was ORIGINALLY booked against — nothing
 * re-derives it from a mid-edit experience switch (the deposit/full-payment picker UI is hidden
 * entirely while editing). A booking that started as full payment (the guide's deliberate original
 * "Collect deposit only" choice, left off) stays full payment through an experience swap — the new
 * experience's own deposit setting doesn't silently opt it into deposit collection the guide never
 * asked for. Only a booking that ALREADY started as deposit-category can be affected by the new
 * experience's `deposit_required` flag on a swap (e.g. downgraded to full payment if the new
 * experience doesn't need one). Otherwise — no swap — the booking's own `payment_option` is the
 * source of truth, which can legitimately differ from what its experience currently supports (e.g.
 * a full-payment booking against a deposit-capable experience must stay full-payment through
 * unrelated price-affecting edits).
 */
export const resolveHasDepositPayment = ({ booking, paymentOption, experienceDetail, isEdit, isExperienceChanged } = {}) => {
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash } = PAYMENT_OPTIONS
  const isDepositCategory = [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(booking?.payment_option || paymentOption)

  if (isEdit && experienceDetail?.id && isExperienceChanged) {
    if (!isDepositCategory) return false
    return !!experienceDetail?.deposit_required
  }

  if (!isDepositCategory) return false

  // payment_option is a durable/historical field - the backend only reconciles it between
  // deposit/full categories on an actual experience swap (see ExperienceUpdater
  // #reconcile_payment_option_for_new_trip!), never on a plain price change (e.g. a guest-count
  // edit). So a booking whose deposit has since been FULLY collected (e.g. price increased after
  // both the deposit and balance were already paid off) keeps a stale deposit-category
  // payment_option forever, even though it's no longer in an active deposit-collection state.
  // Guard against that with the backend's authoritative payment_summary.deposit_due_now
  // (TripBooking#deposit_due_now: the deposit target minus payment_summary.deposit_paid_so_far,
  // never negative) - it's already 0 once the deposit is fully covered, so only treat this as an
  // active deposit scenario while there's genuinely still deposit left to collect. Only applies in
  // edit mode with a persisted payment_summary; a new, not-yet-created booking has none, so it
  // falls through to the category check above (deposit not started yet, category alone decides).
  const depositDueNow = booking?.payment_summary?.deposit_due_now
  if (isEdit && Number.isFinite(depositDueNow)) return depositDueNow > 0

  return true
}

export const calculatePaymentBreakdown = ({
  booking = {},
  bookingSelections = {},
  depositPercent = 0,
  customDepositAmount = 0,
  paymentOption = "",
  isDepositOverride
}) => {
  const { payment_summary = {}, payment_history = [], payment_option } = booking
  const { deposit_later, deposit_cash, deposit_link, deposit_link_in_cash } = PAYMENT_OPTIONS

  const { remaining_balance = 0 } = payment_summary
  const payment_status = booking?.payment_status

  // bookingSelections.amount is already net of the discount (applied before tax in
  // calculateBookingTotal), so it must NOT be discounted again here. It is the grand total and
  // includes the card processing fee on the deposit (see calculateBookingTotal's depositFraction).
  const amount = Math.max(0, bookingSelections?.amount || 0)
  // preCardTotal is the total before any card fee — the base the deposit % is taken from.
  // cardProcessingFee is the deposit's card fee, charged on top of the deposit (paid now).
  const preCardTotal = Math.max(0, bookingSelections?.preCardTotal ?? amount)
  const cardProcessingFee = Math.max(0, bookingSelections?.cardProcessingFee || 0)
  const percent = Number(customDepositAmount || depositPercent) || 0
  // isDepositOverride (see resolveHasDepositPayment) takes precedence in edit mode, where
  // booking.payment_option can be stale relative to a mid-edit experience switch.
  const isDeposit =
    isDepositOverride != null
      ? isDepositOverride
      : [deposit_later, deposit_cash, deposit_link, deposit_link_in_cash].includes(payment_option || paymentOption)

  // A declined charge or a refund the bank refused never moved any money, so it must not count as
  // paid or as given back — the history still lists it, marked failed, for the guide to retry.
  const settledPayments = payment_history?.filter((payment) => !isFailedPaymentStatus(payment?.status)) || []

  // Deposit calculations
  // Prefer the backend's authoritative payment_summary.deposit_paid_so_far (see
  // TripBooking#deposit_paid_so_far) over reconstructing it from payment_history row labels -
  // the two used to diverge (backend treated ALL confirmed income as "paid toward deposit" while
  // this filtered only rows labeled "deposit"), which is exactly what let a booking with 2+ prior
  // incomes skip real deposit collection on edit while this preview showed the correct-looking
  // number. Falls back to the payment_history reconstruction when the field isn't present (e.g.
  // the new-booking creation flow, which has no persisted payment_summary yet) - that fallback
  // reads settledPayments so a declined deposit charge isn't counted as paid.
  const paidDepositAmount = Number.isFinite(payment_summary?.deposit_paid_so_far)
    ? payment_summary.deposit_paid_so_far
    : settledPayments
        .filter((payment) => payment?.type?.toLowerCase() === "deposit")
        .reduce((sum, payment) => sum + (payment?.amount || 0), 0) || 0

  // The deposit % applies to the pre-card total; the card fee (charged only on the amount paid by
  // card now) is then added on top. So the amount due now = deposit base + its card fee.
  const depositBase = (preCardTotal * percent) / 100
  const depositAmount = depositBase + cardProcessingFee
  const depositDueNow = Math.max(0, depositAmount - paidDepositAmount)

  // Balance calculations
  // Exclude tips from total paid amount as they don't reduce the booking balance
  const totalPaidAmount = settledPayments.reduce((sum, payment) => {
    if (payment?.type?.toLowerCase() === "tip") return sum
    return sum + (payment?.amount || 0)
  }, 0)
  const refundDueNow = totalPaidAmount > amount ? roundToCents(Math.abs(totalPaidAmount - amount)) : 0
  // The card processing fee is charged up front with the deposit (full fee when the balance is by
  // card, deposit-only fee when the balance is in cash), so the remaining balance carries no card
  // fee. amount already includes that fee and depositDueNow already accounts for it.
  const balanceAmount = roundToCents(isDeposit ? amount - totalPaidAmount - depositDueNow : amount - totalPaidAmount)

  return {
    // Core values
    amount,
    percent,
    remaining_balance,

    // Deposit related
    depositAmount,
    depositDueNow,
    paidDepositAmount,

    // Payment related
    balanceAmount,

    // Status flags
    payment_status,
    isDeposit,

    // Raw data
    payment_history,
    payment_summary,
    refundDueNow
  }
}
